import { PauseAwareClock, type NowFn } from './clock.ts'
import { BUNDLE_VERSION, MAX_BUNDLE_BYTES, MAX_EVENTS, type Bundle, type BundleEvent } from './bundle.ts'
import type { ElementContext } from './contextTypes.ts'

export interface AudioResult {
  mimeType: string
  bytes: number
  data: Uint8Array | Blob
}

/** The host wraps getUserMedia + MediaRecorder behind this, so the core never touches the browser. */
export interface AudioHandle {
  start(): void
  pause(): void
  resume(): void
  stop(): Promise<AudioResult>
  /** Stops the microphone and discards everything. */
  cancel(): void
  /** Fires when the capture track ends on its own (device unplugged, permission revoked). */
  onEnded(cb: () => void): void
}

export type RecorderState = 'idle' | 'recording' | 'paused' | 'capped' | 'mic-lost' | 'finished'
export type StartOutcome = 'started' | 'mic-denied' | 'mic-missing' | 'mic-error' | 'already-active'

/** Event fields the caller supplies; the recorder adds the time offset and the route. */
export type LoggableEvent =
  | ({ type: 'step'; name: string; role: string } & ElementContext)
  | ({ type: 'pointer'; name: string; role: string; restMs: number; x: number; y: number } & ElementContext)
  | ({ type: 'selection'; name: string; text: string } & ElementContext)
  | { type: 'request'; method: string; path: string; status: number; durationMs: number; correlationId?: string; errorCode?: string }
  | { type: 'console'; level: 'error' | 'warn'; source: string; count: number }

export interface RecorderOptions {
  audioSource: () => Promise<AudioHandle>
  /** Monotonic millisecond source (performance.now). */
  now: NowFn
  /** Current route pattern, never the raw path. */
  route: () => string
  limits: { maxMs: number }
  /** The commit the page was built from; stored with the recording. */
  release?: string
  viewport?: () => { w: number; h: number }
  /** Epoch milliseconds (Date.now); the bundle keeps the instant recording began. Tests inject a fixed clock. */
  wallNow?: () => number
}

export interface FinishedRecording {
  bundle: Bundle
  audio: AudioResult
}

export function createRecorder(options: RecorderOptions) {
  const clock = new PauseAwareClock(options.now)
  let handle: AudioHandle | null = null
  let state: RecorderState = 'idle'
  let events: BundleEvent[] = []
  let droppedEvents = 0
  let droppedRequests = 0
  let droppedConsole = 0
  let bytes = 0
  let startRoute = ''
  let startedAt = 0

  function isLive(): boolean {
    return state === 'recording'
  }

  async function start(): Promise<StartOutcome> {
    if (state !== 'idle') return 'already-active'
    try {
      handle = await options.audioSource()
    } catch (error) {
      const name = (error as { name?: string } | null)?.name
      if (name === 'NotAllowedError' || name === 'SecurityError') return 'mic-denied'
      if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'mic-missing'
      return 'mic-error'
    }
    handle.onEnded(() => {
      if (state === 'recording' || state === 'paused') {
        clock.stop()
        state = 'mic-lost'
      }
    })
    startRoute = options.route()
    startedAt = (options.wallNow ?? Date.now)()
    events = []
    droppedEvents = 0
    droppedRequests = 0
    droppedConsole = 0
    bytes = 0
    clock.start()
    handle.start()
    state = 'recording'
    return 'started'
  }

  function pause(): void {
    if (state !== 'recording') return
    handle?.pause()
    clock.pause()
    state = 'paused'
  }

  function resume(): void {
    if (state !== 'paused') return
    clock.resume()
    handle?.resume()
    state = 'recording'
  }

  function log(event: LoggableEvent): void {
    if (!isLive()) return
    const atMs = clock.offsetMs()
    const withTime = { ...event, atMs } as Record<string, unknown>
    if (event.type === 'step' || event.type === 'pointer' || event.type === 'selection') withTime.route = options.route()
    const size = JSON.stringify(withTime).length + 1 // the comma between events
    // Both caps drop and count: the event count, and a byte budget so rich element context can never push the stored bundle past its 1 MB check.
    if (events.length >= MAX_EVENTS || bytes + size > MAX_BUNDLE_BYTES) {
      droppedEvents += 1
      if (event.type === 'request') droppedRequests += 1
      if (event.type === 'console') droppedConsole += 1
      return
    }
    bytes += size
    events.push(withTime as unknown as BundleEvent)
  }

  /** The host calls this on a short interval; kept separate so tests need no timers. */
  function checkLimits(): void {
    if (state !== 'recording') return
    if (clock.offsetMs() >= options.limits.maxMs) {
      handle?.pause()
      clock.stop()
      state = 'capped'
    }
  }

  function cancel(): void {
    handle?.cancel()
    handle = null
    clock.stop()
    events = []
    state = 'idle'
  }

  async function finish(): Promise<FinishedRecording> {
    if (!handle || state === 'idle' || state === 'finished') throw new Error('No active recording to finish')
    clock.stop()
    const audio = await handle.stop()
    handle = null
    state = 'finished'
    const bundle: Bundle = {
      version: BUNDLE_VERSION,
      app: 'consultant',
      route: startRoute,
      durationMs: Math.max(1, clock.offsetMs()),
      audio: { mimeType: audio.mimeType, bytes: audio.bytes },
      events,
      ...(options.release ? { release: options.release } : {}),
      ...(options.viewport ? { viewport: options.viewport() } : {}),
      startedAt,
      dropped: { events: droppedEvents, requests: droppedRequests, console: droppedConsole },
    }
    return { bundle, audio }
  }

  return {
    get state(): RecorderState {
      return state
    },
    start,
    pause,
    resume,
    log,
    checkLimits,
    cancel,
    finish,
    elapsedMs: () => clock.offsetMs(),
  }
}

export type Recorder = ReturnType<typeof createRecorder>
